import React from 'react';
import { Box } from '@trp-individual-investor-adv-svc/ii-prosp-components';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { createBrowserHistory } from 'history';
import isEmpty from 'lodash.isempty';

import '@trp-beacon-ui-kit/foundation/style-tokens/css/variables.css';
import '@trp-beacon-ui-kit/foundation/fonts/fonts.css';
import '@trp-beacon-ui-kit/foundation/css/style.css';
import '@trp-beacon-ui-kit/foundation/style-tokens/css/theme-trp.css';

import Loading from '../containers/Loading';
import BlueBanner from './navbars/BlueBanner';
import { currentStatus, expiredStatusContent, groupStatus } from './shared/Content';
import { encodedUrlTextNew, log } from '../Utils';
import { rasUrl } from './shared/Links';

import CoClientFailure from './modals/CoClientFailure';
import HubGeneralError from './modals/HubGeneralError';
import AddAccountsModal from './modals/ProspModals/AddAccountsModal';
import PersonalDetailsModal from './modals/ProspModals/PersonalDetailsModal';
import RTQModal from './modals/ProspModals/RTQModal';
import SessionTimer from './modals/SessionTimer';
import CoClientValidationView from '../containers/CoClientValidationView';
import YodleeLinkAccountView from '../containers/YodleeLinkAccountView';
import CoClientEnrolledView from './emailInvitation/CoClientEnrolledView';
import ServiceErrorCard from './shared/ServiceErrorCard';
import connectWithUserStore from '../reducers/connectWithUserStore';
import PinValidationError from './emailInvitation/PinValidationError';
import PinValidationView from './emailInvitation/PinValidation';
import PrimaryLoggedIn from './emailInvitation/PrimaryLoggedIn';
import MonitorLandingPage from './MonitorLandingPage';
import AccountSelection from './AccountSelection';
import EducationModal from './EducationModal';
import PLDLPage from './shared/PLDLPage/PLDLPage';
import AccountSelectionModal from './modals/AccountSelectionModal/AccountSelectionModal';
import EDeliveryModal from './modals/EDeliveryModal/EDeliveryModal';
import CustomerWebView from '../containers/CustomerWebView';
import OutstandingInvite from './modals/OutstandingInvite';
import NewClient from './shared/TransitionPage/NewClient';
import AdviceFooterImpl from './shared/AdviceFooter/AdviceFooterImpl';
import EnvSwitch from './shared/EnvSwitch/EnvSwitch';
import ConsentView from '../containers/ConsentView';
import ScheduleApptModal from './modals/BannerModals/ScheduleApptModal';
import BannerInvitePartnerModal from './modals/BannerModals/BannerInvitePartnerModal';
import CallUsModal from './modals/BannerModals/CallUsModal';
import FAQModal from './modals/BannerModals/FAQModal';
import InvitePartnerModal from './modals/BannerModals/InvitePartnerModal';
import InviteConfirmationModal from './modals/BannerModals/InviteConfirmationModal';
import ConsentGiven from './HubPages/ConsentGiven';
import AppointmentPage from './HubPages/AppointmentPage';
import RasAccessChange from './HubPages/RasAccessChange';
import PlanExpiredPage from './HubPages/PlanExpiredPage';
import SessionLogoutLoader from './modals/SessionLogoutLoader';
import PostConsentInvitePartner from './modals/PostConsentInvitePartner';
import PostConsentInviteConfirmation from './modals/PostConsentInviteConfirmation';
import ScheduleApptErrorModal from './modals/ScheduleApptErrorModal';
import InvitationExpired from './emailInvitation/InvitationExpired';

const MGPHubView = React.lazy(() => import('../containers/MGPHubView'));

const history = createBrowserHistory({});

function PsRoutes({ state, indexState, profiler, dispatch }) {
    const status = state?.engagementStatus?.status;
    const { isCustomerWebView, archivedGroupSummary, planningGroup, currentClientData } = state;

    const urlTextFromInvite = state?.pendingInvite?.encodedUrlText;

    const hasAdvHubEnt = state?.userEntitlements?.includes('ii_adviser_ui_update');
    const hasCoClient = state?.numberOfClients > 1;
    const limbo = archivedGroupSummary?.archivedGroupsCount > 0;
    const isLimbo = limbo && planningGroup === null;
    const serviceType = currentStatus(status, false, archivedGroupSummary, planningGroup);
    const hasDocuments =
        planningGroup?.latestReleasedFinancialPlan ||
        planningGroup?.latestReleasedStrategyComparison ||
        planningGroup?.latestReleasedSelectedStrategy;
    const hasActiveConsent = !isEmpty(currentClientData?.activeConsent)
    const coClientHasActiveConsent = planningGroup?.clients?.some(client => client.clientType !== "PRIMARY")
        && !!planningGroup.clients.filter(client => client.clientType !== "PRIMARY")[0].activeConsent;

    const expiredStatus =
        !isEmpty(currentClientData) && !isLimbo
            ? expiredStatusContent(
                serviceType,
                currentClientData.clientType,
                hasDocuments,
                coClientHasActiveConsent,
                hasCoClient
            )
            : '';
    const routeStatus = !hasActiveConsent ? expiredStatus : serviceType;
    const isRasStatus =
        (routeStatus === groupStatus.IMPLEMENTED ||
            routeStatus === groupStatus.FUNDING ||
            routeStatus === groupStatus.COMPLETED) &&
        hasActiveConsent;
    const isProd =
        !window.location.host.includes('stage') &&
        !window.location.host.includes('dev') &&
        !window.location.host.includes('localhost');
    log(console.log(profiler));

    if (isRasStatus) {
        window.location.replace(`${rasUrl}`);
    }

    if (profiler?.externalUrl) {
        window.location.replace(profiler.externalUrl);
        return null;
    }

    return (
        <div>
            <React.Suspense fallback={<Loading />}>
                <BrowserRouter history={history} basename="/usis/advice/fps">
                    {!isProd && <EnvSwitch state={state} />}
                    <CustomerWebView
                        planningGroup={state.planningGroup}
                        isCustomerWebView={isCustomerWebView}
                        showAdvHubBtn={hasAdvHubEnt}
                    />
                    <BlueBanner state={state} profile={profiler} dispatch={dispatch} />
                    <Box sx={{ inlineSize: 'unset', backgroundColor: '#F4F5F6' }}>
                        <Routes>
                            <Route
                                exact
                                path="/transition"
                                element={
                                    profiler?.route === '/transition' ? (
                                        <NewClient
                                            state={state}
                                            dispatch={dispatch}
                                            profile={profiler}
                                        />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/monitor"
                                element={
                                    profiler?.route === '/monitor' ? (
                                        <MonitorLandingPage
                                            state={state}
                                            dispatch={dispatch}
                                        />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/account-selection"
                                element={
                                    profiler?.route === '/account-selection' ? (
                                        <AccountSelection
                                            state={state}
                                            profile={profiler}
                                            dispatch={dispatch}
                                        />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/"
                                element={<Navigate push to={profiler?.route} />}
                            />
                            <Route
                                exact
                                path="/client-consent"
                                element={
                                    profiler?.route === '/client-consent' ? (
                                        <ConsentView profiler={profiler} />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/client-consent-reenroll"
                                element={
                                    profiler?.route === '/client-consent-reenroll' ? (
                                        <ConsentView profiler={profiler} />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/co-client-consent"
                                element={
                                    profiler?.route === '/co-client-consent' ? (
                                        <ConsentView profiler={profiler} />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/co-client-consent-reenroll"
                                element={
                                    profiler?.route === '/co-client-consent-reenroll' ? (
                                        <ConsentView profiler={profiler} />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                path="/invite/:encodedUrlText"
                                element={<CoClientValidationView />}
                            />
                            <Route
                                exact
                                path="client-reenroll"
                                element={
                                    profiler?.route === '/client-reenroll' ? (
                                        <PlanExpiredPage state={state} dispatch={dispatch} />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/co-client-reenroll"
                                element={
                                    profiler?.route === '/co-client-reenroll' ? (
                                        <PlanExpiredPage state={state} dispatch={dispatch} />
                                    ) : (
                                        <Navigate push to={profiler?.route ?? '/'} />
                                    )
                                }
                            />

                            <Route
                                exact
                                path="/client-expired"
                                element={
                                    profiler?.route === '/client-expired' ? (
                                        <PlanExpiredPage state={state} dispatch={dispatch} />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/co-client-expired"
                                element={
                                    profiler?.route === '/co-client-expired' ? (
                                        <>
                                            <PlanExpiredPage
                                                state={state}
                                                dispatch={dispatch}
                                            />
                                        </>
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/consent-given"
                                element={
                                    profiler?.route === '/consent-given' ? (
                                        <>
                                            <ConsentGiven state={state} dispatch={dispatch} />
                                        </>
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/consent-given-reenroll"
                                element={
                                    profiler?.route === '/consent-given-reenroll' ? (
                                        <ConsentGiven state={state} dispatch={dispatch} />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/appointment-info"
                                element={
                                    profiler?.route === '/appointment-info' ? (
                                        <AppointmentPage state={state} dispatch={dispatch} />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/appointment-info-reenroll"
                                element={
                                    profiler?.route === '/appointment-info-reenroll' ? (
                                        <AppointmentPage state={state} dispatch={dispatch} />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/new-plan"
                                element={
                                    profiler?.route === '/new-plan' ? (
                                        <RasAccessChange
                                            state={state}
                                            dispatch={dispatch}
                                        />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                exact
                                path="/plan-delivered"
                                element={
                                    profiler?.route === '/plan-delivered' ? (
                                        <PLDLPage state={state} dispatch={dispatch} />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                path="/invitation-service-error"
                                element={
                                    encodedUrlTextNew() ? (
                                        <ServiceErrorCard />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route path="/enrolled" element={<CoClientEnrolledView />} />
                            <Route path="/primaryloggedin" element={<PrimaryLoggedIn />} />
                            <Route path="/pin-entry" element={<PinValidationView />} />
                            <Route
                                path="/pin-max-attempts"
                                element={
                                    encodedUrlTextNew() || urlTextFromInvite ? (
                                        <PinValidationError />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                path="/profile"
                                element={<MGPHubView profiler={profiler} />}
                            />
                            <Route
                                path="/invitation-expired"
                                element={
                                    encodedUrlTextNew() ? (
                                        <InvitationExpired />
                                    ) : (
                                        <Navigate to={profiler?.route ?? '/'} />
                                    )
                                }
                            />
                            <Route
                                path="/yodlee-link-account"
                                element={<YodleeLinkAccountView indexState={indexState} />}
                            />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                        <PostConsentInvitePartner state={state} />
                        <PostConsentInviteConfirmation />
                        <CoClientFailure />
                        <HubGeneralError />
                        <EducationModal dispatch={dispatch} />
                        <EDeliveryModal dispatch={dispatch} />
                        <AccountSelectionModal state={state} dispatch={dispatch} />
                        <OutstandingInvite state={state} />
                        <PersonalDetailsModal state={state} dispatch={dispatch} />
                        <RTQModal state={state} dispatch={dispatch} />
                        <AddAccountsModal state={state} dispatch={dispatch} />
                        <ScheduleApptModal state={state} />
                        <ScheduleApptErrorModal state={state} dispatch={dispatch} />
                        <BannerInvitePartnerModal isCustomerWebView={isCustomerWebView} />
                        <CallUsModal />
                        <FAQModal />
                        <InvitePartnerModal state={state} />
                        <InviteConfirmationModal />
                        <SessionLogoutLoader />
                        <SessionTimer state={state} />
                    </Box>
                    <AdviceFooterImpl noPt />
                </BrowserRouter>
            </React.Suspense>
        </div>
    );
}

export default connectWithUserStore(PsRoutes);
